import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./style/tutoringTableStyle.css";

/**
 * Componente de tabla CONTROLADO.
 * - El padre controla: data, sort, filters, paginación.
 * - La tabla sólo emite eventos (onSortChange, onFilterChange, onPageChange...).
 * - Celdas ricas mediante column.render(row) o column.type ("text"|"image"|"button"|"select").
 * 
 * Propiedades:
 * columns: Array<Column>
 * --------------------------------------------
 * Descripción general:
 * - Cada objeto del arreglo define una columna de la tabla.
 * - Requeridos: `key`, `header`.
 * - Si existe `render(row)`, tiene prioridad sobre `type`.
 *
 * Campos de Column:
 * - key: string            (REQUERIDO)
 *    · Identificador único de la columna y nombre de la propiedad en cada fila (row[key]).
 *
 * - header: ReactNode      (REQUERIDO)
 *    · Título mostrado en el <th> (texto o JSX).
 *
 * - width: number|string   (OPCIONAL)
 *    · Ancho de columna (ej. 120, "20%", "8rem"). No aplica estilos si se omite.
 *
 * - sortable: boolean      (OPCIONAL; default: false)
 *    · Si true, el <th> emite onSortChange({ key, direction }).
 *
 * - type: "text"|"image"|"button"|"select"   (OPCIONAL)
 *    · Render predefinido de la celda. Ignorado si existe `render`.
 *
 * - render: (row, rowIndex) => ReactNode     (OPCIONAL)
 *    · Render totalmente custom de la celda. Si se define, ignora `type`.
 *
 * - filter: {                                   (OPCIONAL)
 *     type: "text"|"select"|"dateRange",        (REQUERIDO si hay filter)
 *     name?: string,                             (OPCIONAL; default: key)
 *     label?: ReactNode,                         (OPCIONAL; default: header)
 *     placeholder?: string,                      (OPCIONAL; solo text)
 *     options?: Array<{ value:any, label:node }> (OPCIONAL; requerido para select)
 *   }
 *    · Dibuja un control en la toolbar. El estado vive en `filters` del padre.
 *
 * - buttonLabel: string     (OPCIONAL; solo si type="button")
 * - onClick: (row) => void  (OPCIONAL; solo si type="button")
 *
 * - options: Array<{ value:any, label:node }>        (OPCIONAL; solo si type="select")
 * - onSelectChange: (row, value) => void             (OPCIONAL; solo si type="select")
 *
 * Notas de comportamiento:
 * - type="text": muestra row[key] como texto plano si no hay `render`.
 * - type="image": acepta row[key] como string (URL) o { src, alt }.
 * - type="button": renderiza un botón por fila; usa `buttonLabel` y `onClick(row)`.
 * - type="select": renderiza un <select> por fila; usa `options` y `onSelectChange(row,val)`.
 * - filter.select: `options` debe incluir al menos una opción (ej. "Todos").
 *
 * Ejemplos:
 * // Texto + filtro de búsqueda y sort
 * { key: "id", header: "ID", sortable: true, filter: { type: "text", name: "q", label: "Buscar" } }
 *
 * // Select de toolbar (filtro) + columnas ordenables
 * { key: "role", header: "Role", sortable: true, filter: {
 *     type: "select", name: "role", label: "Role",
 *     options: [{ value: "", label: "All" }, { value: "Student", label: "Student" }]
 * }}
 *
 * // Imagen
 * { key: "avatar", header: "Avatar", type: "image" }       // row.avatar = "/img/u1.png" o {src, alt}
 *
 * // Botón por fila
 * { key: "actions", header: "Actions", type: "button", buttonLabel: "Edit", onClick: (row) => edit(row) }
 *
 * // Select por fila
 * { key: "status", header: "Status", type: "select",
 *   options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }],
 *   onSelectChange: (row, val) => updateStatus(row.id, val)
 * }
 *
 * // Render custom (ignora type) — ejemplo para badge/estado
 * { key: "vote", header: "Vote", sortable: true,
 *   render: (row) => (<><span className={`vote__dot ${row.vote}`}/> {row.vote}</>)
 * }
 * 
 * * EVENTOS (el padre hace el trabajo):
 * - onSortChange({key, direction})
 * - onFilterChange(nextFilters)
 * - onPageChange(nextPage)
 * - onRowClick?(row)
 */

function DataTableComponent({
  columns,
  data,
  sort,
  onSortChange,
  filters,
  onFilterChange,
  page,
  pageSize,
  total,
  onPageChange,
  stickyHeader = true,
  maxHeight = 480,
  loading = false,
  emptyState = "Sin datos",
  title,
  actions,
  className = "",
  rowKey,
  onRowClick,
}) {
  const totalPages = Math.max(1, Math.ceil((total ?? data.length) / pageSize));

  const handleSort = (key, sortable) => {
    if (!sortable) return;
    const direction = sort?.key === key && sort?.direction === "asc" ? "desc" : "asc";
    onSortChange({ key, direction });
  };

  const handleFilterChange = (name, value) => {
    const next = { ...(filters || {}), [name]: value };
    onFilterChange(next);
  };

  const startIdx = (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total ?? data.length);

  const pageNumbers = (() => {
    // ventana de 3 páginas como en tu patrón
    const current = page;
    const start = Math.max(1, current - 1);
    const end = Math.min(totalPages, start + 2);
    const fixedStart = Math.max(1, end - 2);
    const arr = [];
    for (let i = fixedStart; i <= end; i++) arr.push(i);
    return arr;
  })();

  const renderSortIcon = (col) => {
    if (!col.sortable) return <span className="dataTable__th__sort dataTable__th__sort--off">⇅</span>;
    if (sort?.key !== col.key) return <span className="dataTable__th__sort">⇅</span>;
    return (
      <span className={`dataTable__th__sort ${sort.direction === "asc" ? "asc" : "desc"}`}>
        {sort.direction === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const cellRenderer = (col, row, rowIndex) => {
    if (typeof col.render === "function") return col.render(row, rowIndex);

    switch (col.type) {
      case "image":
        return (
          <div className="dataTable__cellImage">
            {/* row[col.key] puede ser URL string o {src, alt} */}
            {(() => {
              const v = row[col.key];
              if (!v) return null;
              if (typeof v === "string") return <img src={v} alt="" />;
              return <img src={v.src} alt={v.alt || ""} />;
            })()}
          </div>
        );
      case "button":
        return (
          <button
            type="button"
            className="dataTable__cellButton"
            onClick={(e) => {
              e.stopPropagation();
              col.onClick(row);
            }}
          >
            {col.buttonLabel || "Acción"}
          </button>
        );
      case "select":
        return (
          <div className="dataTable__cellSelect">
            <select
              value={row[col.key] ?? ""}
              onChange={(e) => col.onSelectChange?.(row, e.target.value)}
            >
              {(col.options || []).map((opt) => (
                <option key={String(opt.value)} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        );
      default:
        return <span className="dataTable__cellText">{row[col.key]}</span>;
    }
  };

  return (
    <div className={`dataTable ${className}`.trim()}>
      {(title || actions) && (
        <div className="dataTable__header">
          {title && <h2 className="dataTable__header__title">{title}</h2>}
          {actions && <div className="dataTable__header__actions">{actions}</div>}
        </div>
      )}

      {/* Toolbar de filtros controlados */}
      <div className="dataTable__toolbar">
        <div className="dataTable__toolbar__filters">
          {columns
            .filter((c) => c.filter)
            .map((c) => {
              const f = c.filter; // { type: 'text'|'select'|'dateRange', name, placeholder, options }
              const name = f.name || c.key;
              const value = filters?.[name] ?? "";

              if (f.type === "select") {
                return (
                  <label key={name} className="dataTable__filter dataTable__filter--select">
                    <span className="dataTable__filter__label">{f.label || c.header}</span>
                    <div className="input-wrapper">
                      <select
                        value={value}
                        onChange={(e) => handleFilterChange(name, e.target.value)}
                      >
                        {(f.options || []).map((opt) => (
                          <option key={String(opt.value)} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </label>
                );
              }

              if (f.type === "text") {
                return (
                  <label key={name} className="dataTable__filter dataTable__filter--text">
                    <span className="dataTable__filter__label">{f.label || c.header}</span>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        placeholder={f.placeholder || "Buscar..."}
                        value={value}
                        onChange={(e) => handleFilterChange(name, e.target.value)}
                      />
                    </div>
                  </label>
                );
              }

              if (f.type === "dateRange") {
                const from = value?.from ?? "";
                const to = value?.to ?? "";
                return (
                  <div key={name} className="dataTable__filter dataTable__filter--daterange">
                    <span className="dataTable__filter__label">{f.label || c.header}</span>
                    <div className="dataTable__filter__range">
                      <input
                        type="date"
                        value={from}
                        onChange={(e) => handleFilterChange(name, { ...value, from: e.target.value })}
                      />
                      <span>—</span>
                      <input
                        type="date"
                        value={to}
                        onChange={(e) => handleFilterChange(name, { ...value, to: e.target.value })}
                      />
                    </div>
                  </div>
                );
              }

              return null;
            })}
        </div>
        <div className="dataTable__toolbar__meta">
          <span className="dataTable__toolbar__count">
            {total ? `${startIdx}-${endIdx} de ${total}` : `${startIdx}-${endIdx}`}
          </span>
        </div>
      </div>

      <div
        className={`dataTable__container ${stickyHeader ? "sticky" : ""}`}
        style={{ maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight }}
      >
        <table className="dataTable__table" role="table" aria-busy={loading}>
          <thead className="dataTable__thead">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`dataTable__th ${col.sortable ? "sortable" : ""}`}
                  onClick={() => handleSort(col.key, col.sortable)}
                  scope="col"
                  style={{ width: col.width }}
                >
                  <span className="dataTable__th__label">{col.header}</span>
                  {renderSortIcon(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="dataTable__tbody">
            {loading && (
              <tr className="is-loading">
                <td colSpan={columns.length}>Cargando…</td>
              </tr>
            )}

            {!loading && data.length === 0 && (
              <tr className="is-empty">
                <td colSpan={columns.length}>{emptyState}</td>
              </tr>
            )}

            {!loading &&
              data.map((row, idx) => {
                const key = rowKey ? rowKey(row, idx) : `${idx}`;
                return (
                  <tr
                    key={key}
                    className="dataTable__tr"
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className="dataTable__td">
                        {cellRenderer(col, row, idx)}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Paginación controlada */}
      <div className="dataTable__pagination">
        <button
          className="dataTable__pagination__btn"
          onClick={() => onPageChange?.(Math.max(1, page - 1))}
          disabled={page === 1}
        >
          ◀
        </button>
        {pageNumbers.map((n) => (
          <button
            key={n}
            className={`dataTable__pagination__btn ${n === page ? "active" : ""}`}
            onClick={() => onPageChange?.(n)}
          >
            {n}
          </button>
        ))}
        <button
          className="dataTable__pagination__btn"
          onClick={() => onPageChange?.(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
        >
          ▶
        </button>
      </div>
    </div>
  );
}

DataTableComponent.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.node.isRequired,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      sortable: PropTypes.bool,
      type: PropTypes.oneOf(["text", "image", "button", "select"]),
      render: PropTypes.func,
      filter: PropTypes.shape({
        type: PropTypes.oneOf(["text", "select", "dateRange"]).isRequired,
        name: PropTypes.string,
        label: PropTypes.node,
        placeholder: PropTypes.string,
        options: PropTypes.arrayOf(
          PropTypes.shape({ value: PropTypes.any, label: PropTypes.node })
        ),
      }),
      onClick: PropTypes.func,
      options: PropTypes.array,
      onSelectChange: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  sort: PropTypes.shape({ key: PropTypes.string, direction: PropTypes.oneOf(["asc", "desc"]) }),
  onSortChange: PropTypes.func,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  total: PropTypes.number,
  onPageChange: PropTypes.func,
  stickyHeader: PropTypes.bool,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
  emptyState: PropTypes.node,
  title: PropTypes.node,
  actions: PropTypes.node,
  className: PropTypes.string,
  rowKey: PropTypes.func,
  onRowClick: PropTypes.func,
};

export default DataTableComponent;